import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getAssetType,
  createAssetType,
  updateAssetType,
  deleteAssetType,
  getAssetTypeByName,
} from '../api/assetTypeAPI';
import { getPlanningType } from '../api/planning-typeAPI';
import { getMeters } from '../api/metersAPI';
import { fetchData } from '../utils/apiHelpers';

const AssetTypeListLayer = () => {
  const userProfile = useUserProfile();
  const [assetTypeList, setAssetTypeList] = useState([]);
  const [planningTypes, setPlanningTypes] = useState([]);
  const [meters, setMeters] = useState([]);
  const [newType, setNewType] = useState({
    mandatorySerialNumber: 0,
    noDuplicateSerialNumbers: 0,
    examples: '',
    defaultMeterId: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchAssetTypes = () => {
    fetchData(getAssetType, {}, setAssetTypeList);
  };

  const fetchPlanningTypes = () => {
    fetchData(getPlanningType, {}, setPlanningTypes);
  };

  const fetchMeters = () => {
    fetchData(getMeters, {}, setMeters);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newType.name?.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchAssetTypes();
    fetchPlanningTypes();
    fetchMeters();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (assetTypeList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [assetTypeList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'defaultPlanningType') {
      const selected = planningTypes.find((pt) => pt.id === value);
      setNewType((prev) => ({
        ...prev,
        [name]: value,
        defaultMeterId: selected?.typeDescription === 'Basic' ? '' : prev.defaultMeterId,
      }));
    } else {
      setNewType((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleToggleChange = (field, checked) => {
    setNewType((prev) => ({ ...prev, [field]: checked ? 1 : 0 }));
  };

  const handleEdit = (item) => {
    setNewType({
      ...item,
      mandatorySerialNumber: item.mandatorySerialNumber ? 1 : 0,
      noDuplicateSerialNumbers: item.noDuplicateSerialNumbers ? 1 : 0,
      defaultPlanningType: item.defaultPlanningType,
      defaultMeterId: item.defaultMeterId || null,
      examples: item.examples || '',
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset type?')) return;
    try {
      await deleteAssetType(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchAssetTypes();
    } catch (error) {
      console.error('Error deleting asset type:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const existing = await getAssetTypeByName(newType.name);
      if (existing && existing.id !== editingId) {
        setErrors({ name: 'Asset Type already exists' });
        return;
      }
      const payload = { ...newType };
      const selected = planningTypes.find((pt) => pt.id === newType.defaultPlanningType);
      if (selected?.typeDescription === 'Basic') {
        payload.defaultMeterId = null;
      }
      const cleanPayload = {};
      for (const key in payload) {
        if (typeof payload[key] !== 'object' && (payload[key] !== null || key === 'defaultMeterId')) {
          cleanPayload[key] = payload[key];
        }
      }
      if (editingId) {
        await updateAssetType(editingId, cleanPayload);
      } else {
        await createAssetType(cleanPayload);
      }
      setShowModal(false);
      setEditingId(null);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      setNewType({
        mandatorySerialNumber: 0,
        noDuplicateSerialNumbers: 0,
        examples: '',
        defaultMeterId:null,
      });
      await fetchAssetTypes();
    } catch (error) {
      console.error('Error saving asset type:', error);
    }
  };

  const selectedPlanningType = planningTypes.find(
    (pt) => pt.id === newType.defaultPlanningType
  );
  const isBasicPlanningType = selectedPlanningType?.typeDescription === 'Basic';

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('asset-type:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewType({
                mandatorySerialNumber: 0,
                noDuplicateSerialNumbers: 0,
                examples: '',
                defaultMeterId: null,
              });
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Asset Type
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Examples</th>
                <th>Mandatory Serial Number</th>
                <th>Default Planning Type</th>
                <th>Default Meter</th>
                <th>No Duplicate Serial Numbers</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assetTypeList.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td>{item.examples}</td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3">
                      <input className="form-check-input" type="checkbox" role="switch" checked={item.mandatorySerialNumber === 1} disabled />
                    </div>
                  </td>
                  <td>{item.defaultPlanningType2?.typeDescription || ''}</td>
                  <td>{item.defaultMeter?.name || ''}</td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3">
                      <input className="form-check-input" type="checkbox" role="switch" checked={item.noDuplicateSerialNumbers === 1} disabled />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('asset-type:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('asset-type:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Asset Type' : 'New Asset Type'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">Name *</label>
                    <input
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newType.name || ''}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Examples</label>
                    <input
                      className="form-control"
                      name="examples"
                      value={newType.examples || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Mandatory Serial Number</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={newType.mandatorySerialNumber === 1}
                        onChange={(e) => handleToggleChange('mandatorySerialNumber', e.target.checked)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">No Duplicate Serial Numbers</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={newType.noDuplicateSerialNumbers === 1}
                        onChange={(e) => handleToggleChange('noDuplicateSerialNumbers', e.target.checked)}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Default Planning Type</label>
                    <select
                      className="form-select"
                      name="defaultPlanningType"
                      value={newType.defaultPlanningType || ''}
                      onChange={handleInputChange}
                    >
                      <option value=''>Select Planning Type</option>
                      {planningTypes.map((pt) => (
                        <option key={pt.id} value={pt.id}>{pt.typeDescription}</option>
                      ))}
                    </select>
                  </div>
                  {!isBasicPlanningType && (
                    <div className="col-md-12">
                      <label className="form-label">Default Meter</label>
                      <select
                        className="form-select"
                        name="defaultMeterId"
                        value={newType.defaultMeterId || null}
                        onChange={handleInputChange}
                      >
                        <option value=''>Select Meter</option>
                        {meters.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetTypeListLayer;
